<template>
  <!-- 按鈕 (取得 ref) -->
  <button class="custom-btn" ref="buttonRef" @click="togglePopover">
    <img class="undo-img" src="@/assets/images/admin/cashpayments/undo.png" alt="Undo Icon">
  </button>

  <!-- 使用 Teleport 將 popover 渲染到 <body> 下 -->
  <teleport to="body">
    <div 
      v-if="showConfirm" 
      class="confirm-popover" 
      ref="popoverRef"
    >
      <p class="confirm-message">Are you sure you want to undo this settlement?</p>
      <div class="confirm-actions">
        <button class="cancel-btn" @click="onCancel">Cancel</button>
        <button class="undo-btn" @click="onConfirm">Yes undo</button>
      </div>
      <div class="popover-arrow" ref="arrowRef"></div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted, defineProps } from 'vue';
import { computePosition, autoUpdate, offset, arrow } from '@floating-ui/dom';
import { usePaymentListStore } from '@/store/admin/cashPayment/paymentList';

const paymentListStore = usePaymentListStore();

const props = defineProps({
  id: { type: [String, Number], required: true }
});

const showConfirm = ref(false);
const buttonRef = ref(null);
const popoverRef = ref(null);
const arrowRef = ref(null);
let cleanup = null;

// 使用 Floating UI，強制設定在按鈕上方（placement: 'top'）
const updatePosition = async () => {
  if (buttonRef.value && popoverRef.value) {
    const { x, y, middlewareData } = await computePosition(
      buttonRef.value,
      popoverRef.value,
      {
        placement: 'top',
        middleware: [
          offset(10),
          arrow({ element: arrowRef.value })
        ],
      }
    );
    Object.assign(popoverRef.value.style, {
      left: `${x}px`,
      top: `${y}px`,
      position: 'absolute'
    });
    if (middlewareData.arrow) {
      Object.assign(arrowRef.value.style, {
        left: `${middlewareData.arrow.x}px`
      });
    }
  }
};

// 當切換 popover 顯示時，同時發送全域事件通知其他元件關閉自己的 popover
const togglePopover = () => {
  if (showConfirm.value) {
    showConfirm.value = false;
    if (cleanup) cleanup();
  } else {
    // 發送事件：讓其他元件收到通知關閉它們的 popover
    window.dispatchEvent(new CustomEvent('closeUndoPopovers', { detail: { id: props.id } }));
    showConfirm.value = true;
    nextTick(() => {
      if (buttonRef.value && popoverRef.value) {
        cleanup = autoUpdate(buttonRef.value, popoverRef.value, updatePosition);
      }
    });
  }
};

const onCancel = () => {
  showConfirm.value = false;
  if (cleanup) cleanup();
};

const onConfirm = async () => {
  showConfirm.value = false;
  if (cleanup) cleanup();
  try {
    // 傳遞支付ID至後端
    await paymentListStore.undoPayment(props.id);
    await paymentListStore.fetchPayments();
  } catch (error) {
    console.error('undo 動作失敗:', error);
  }
};

// 當收到全域 closeUndoPopovers 事件，若 id 不同則關閉 popover
const onCloseAll = (event) => {
  if (event.detail && event.detail.id !== props.id) {
    showConfirm.value = false;
    if (cleanup) cleanup();
  }
};

// 新增：偵測點擊元件外部的事件
const handleClickOutside = (event) => {
  if (showConfirm.value && popoverRef.value && buttonRef.value) {
    // 若點擊位置既不在按鈕內也不在 popover 內，則關閉 popover
    if (!buttonRef.value.contains(event.target) && !popoverRef.value.contains(event.target)) {
      showConfirm.value = false;
      if (cleanup) cleanup();
    }
  }
};

onMounted(() => {
  window.addEventListener('closeUndoPopovers', onCloseAll);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('closeUndoPopovers', onCloseAll);
  document.removeEventListener('click', handleClickOutside);
  if (cleanup) cleanup();
});
</script>

<style scoped>
.custom-btn {
  background: transparent;
  border: none;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease-in-out;
  width: 40px;
  height: 40px;
}

.custom-btn:hover {
  border: 1px solid #c3c3c3;
}

.undo-img {
  width: 25px;
  height: 25px;
}

/* popover 主體 */
.confirm-popover {
  background: #fff;
  padding: 20px;
  border-radius: 0.5rem;
  width: 450px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1200;
}

/* 箭頭 */
.popover-arrow {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #fff;
}

/* 文字內容 */
.confirm-message {
  margin-bottom: 1rem;
  font-size: 1rem;
  color: #333;
  text-align: center;
}

/* 按鈕容器 */
.confirm-actions {
  display: flex;
  justify-content: flex-end;
}

/* Cancel 按鈕 */
.cancel-btn {
  background-color: transparent;
  border: none;
  color: #6c757d;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.15s ease-in-out;
}

.cancel-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Yes undo 按鈕 */
.undo-btn {
  border: none;
  color: #8476f5;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 1rem;
  cursor: pointer;
  background-color: #fff;
}
</style>
